import { STATUS_STYLES } from "@/lib/constants";
import { orderFlow, stageViews, currentStageLabel } from "@/lib/stages";
import { GitBranch } from "lucide-react";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN"); // production units are whole pieces
const ORDER = { "In Progress": 0, Delayed: 1, Pending: 2 };

function Legend() {
  return (
    <div className="flex items-center gap-4 text-[11px] text-slate-500 flex-wrap" data-testid="wip-legend">
      <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-blue-500 inline-block" />In-House</span>
      <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-orange-500 inline-block" />OSP (At Vendor)</span>
      <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-500 inline-block" />Finished</span>
      <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-slate-200 inline-block" />Remaining</span>
    </div>
  );
}

function StageSequence({ order }) {
  const views = stageViews(order);
  const current = currentStageLabel(order);
  return (
    <p className="text-[11px] text-slate-500 truncate" title={views.map((v) => v.name).join(" → ")}>
      {views.map((v, i) => (
        <span key={`${v.name}-${i}`}>
          <span className={v.name === current && !v.isDone ? (v.isOsp ? "text-orange-600 font-medium" : "text-blue-600 font-medium") : v.isDone ? "text-emerald-600" : ""}>
            {v.name}{v.isOsp ? " (OSP)" : ""}
          </span>
          {i < views.length - 1 && " → "}
        </span>
      ))}
    </p>
  );
}

function FlowBar({ order }) {
  const flow = orderFlow(order);
  const pct = (n) => (flow.target > 0 ? (n / flow.target) * 100 : 0);
  return (
    <div>
      <div className="h-5 w-full rounded-md bg-slate-200 overflow-hidden flex" data-testid={`wip-bar-${order.order_code}`}>
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct(flow.finished)}%` }} title={`Finished ${fmt(flow.finished)}`} />
        <div className="h-full bg-orange-500 transition-all" style={{ width: `${pct(flow.atVendor)}%` }} title={`At Vendor ${fmt(flow.atVendor)}`} />
        <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct(flow.inHouse)}%` }} title={`In-House ${fmt(flow.inHouse)}`} />
      </div>
      <div className="flex justify-between text-[10px] font-mono-data text-slate-400 mt-1">
        <span>Finished {fmt(flow.finished)} · At Vendor {fmt(flow.atVendor)} · In-House {fmt(flow.inHouse)} · Remaining {fmt(flow.remaining)}</span>
        <span>{fmt(flow.target)} pcs</span>
      </div>
    </div>
  );
}

const HIDDEN = new Set(["Completed", "Cancelled"]);

export function OrderFlowBar({ order }) {
  return <FlowBar order={order} />;
}

export default function WipPipeline({ orders = [] }) {
  const rows = orders
    .filter((o) => !HIDDEN.has(o.status) && (o.stages || []).length > 0)
    .sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9));
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="wip-pipeline">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><GitBranch className="h-4 w-4 text-indigo-600" /> WIP Pipeline</h3>
          <p className="text-xs text-slate-400">{rows.length} open order{rows.length !== 1 ? "s" : ""} · quantity flow by stage</p>
        </div>
        <Legend />
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">No open orders in the pipeline.</p>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto" data-testid="wip-rows">
          {rows.map((o) => {
            const s = STATUS_STYLES[o.status] || STATUS_STYLES.Pending;
            const flow = orderFlow(o);
            const finishedPct = flow.target > 0 ? Math.round((flow.finished / flow.target) * 100) : 0;
            return (
              <div key={o.id} className="px-5 py-3 grid grid-cols-1 md:grid-cols-[150px_220px_1fr_130px] items-center gap-3 md:gap-4" data-testid={`wip-row-${o.order_code}`}>
                <div className="min-w-0">
                  <p className="font-mono-data text-xs font-semibold text-slate-700">{o.order_code}</p>
                  <p className="text-[11px] text-slate-400 truncate">{o.product_name}</p>
                </div>
                <StageSequence order={o} />
                <FlowBar order={o} />
                <div className="md:text-right">
                  <span className={`text-[10px] font-mono-data uppercase tracking-wide px-2 py-1 rounded ${s.badge}`}>{o.status}</span>
                  <p className="text-[10px] text-slate-400 mt-1" data-testid={`wip-finished-${o.order_code}`}>{finishedPct}% finished</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
